// Maps the current DOM text selection onto a reader paragraph so we can offer a
// selection menu and splice a reworded phrase back into the segment. Ported from
// TransliFlow; framework-agnostic.

export interface ReaderSelection {
  segmentId: string; // value of the paragraph's data-reader-seg attribute
  text: string; // the exact selected substring
  base: string; // the paragraph's full text (the splice target)
  start: number; // char offset of the selection start within `base`
  end: number; // char offset of the selection end within `base`
  rect: DOMRect; // bounding box of the selection (viewport coords)
}

// Char offset from the start of `root`'s text content up to (node, offset).
function offsetWithin(root: Node, node: Node, offset: number): number {
  const range = document.createRange();
  range.selectNodeContents(root);
  range.setEnd(node, offset);
  return range.toString().length;
}

// Returns the reader paragraph (the [data-reader-seg] element) containing a node.
function paragraphOf(node: Node | null): HTMLElement | null {
  const el = node instanceof Element ? node : (node?.parentElement ?? null);
  return el?.closest<HTMLElement>('[data-reader-seg]') ?? null;
}

// Inverse of offsetWithin: resolves a char offset into a concrete (text node,
// offset) pair. Clamps past-the-end offsets to the last text node.
function locateOffset(root: Node, target: number): { node: Node; offset: number } {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let acc = 0;
  let last: Node = root;
  for (let n = walker.nextNode(); n; n = walker.nextNode()) {
    const len = n.textContent?.length ?? 0;
    if (target <= acc + len) return { node: n, offset: target - acc };
    acc += len;
    last = n;
  }
  return { node: last, offset: last.textContent?.length ?? 0 };
}

const isWordChar = (ch: string | undefined) => !!ch && /[\p{L}\p{N}]/u.test(ch);
const isSpaceChar = (ch: string | undefined) => !!ch && /\s/.test(ch);
// Anything that is neither a word char nor whitespace — quotes, dashes, and the
// run-together terminators like "?!" or "..." we want to grab as one token.
const isPunctChar = (ch: string | undefined) => !!ch && !isWordChar(ch) && !isSpaceChar(ch);

// The token boundaries in `base` around a caret offset `at`. A token is a run of
// word chars or a run of punctuation: words snap to word boundaries, while a run
// of punctuation snaps whole (so "?!" / "..." come out as one unit). The caret
// sits between base[at-1] and base[at]; we prefer the word on either side, then
// fall back to a punctuation run. Returns null over whitespace or empty text.
export function tokenRangeAt(base: string, at: number): { start: number; end: number } | null {
  const inClass =
    isWordChar(base[at - 1]) || isWordChar(base[at])
      ? isWordChar
      : isPunctChar(base[at - 1]) || isPunctChar(base[at])
        ? isPunctChar
        : null;
  if (!inClass) return null;
  let s = at;
  let e = at;
  while (s > 0 && inClass(base[s - 1])) s--;
  while (e < base.length && inClass(base[e])) e++;
  return e > s ? { start: s, end: e } : null;
}

export interface WordRange {
  para: HTMLElement;
  start: number;
  end: number;
}

// The (text node, offset) under a viewport point, across the two browser APIs.
export function caretFromPoint(x: number, y: number): { node: Node; offset: number } | null {
  const doc = document as Document & {
    caretRangeFromPoint?(x: number, y: number): Range | null;
    caretPositionFromPoint?(x: number, y: number): { offsetNode: Node; offset: number } | null;
  };
  if (doc.caretRangeFromPoint) {
    const r = doc.caretRangeFromPoint(x, y);
    return r ? { node: r.startContainer, offset: r.startOffset } : null;
  }
  if (doc.caretPositionFromPoint) {
    const p = doc.caretPositionFromPoint(x, y);
    return p ? { node: p.offsetNode, offset: p.offset } : null;
  }
  return null;
}

// The whole token under a viewport point, as char offsets into its reader
// paragraph. A token is a run of word chars or a run of punctuation, so dragging
// onto a word's trailing "?!" or "..." picks it up as a unit. Returns null when
// the point isn't on a token inside a [data-reader-seg] paragraph — this is how we
// replace native selection: we hit-test ourselves.
export function wordRangeAtPoint(x: number, y: number): WordRange | null {
  const caret = caretFromPoint(x, y);
  if (!caret || caret.node.nodeType !== Node.TEXT_NODE) return null;
  const para = paragraphOf(caret.node);
  if (!para?.dataset.readerSeg) return null;
  const base = para.textContent ?? '';
  const at = offsetWithin(para, caret.node, caret.offset);
  const range = tokenRangeAt(base, at);
  return range ? { para, start: range.start, end: range.end } : null;
}

// A reading anchor: a char offset inside a specific reader paragraph. This is
// the resolution-independent cursor we persist (in the URL as a word offset) so
// reopening a book lands on the same word regardless of device or page size.
export interface ReaderAnchor {
  segmentId: string;
  charOffset: number;
}

// The caret under a viewport point, resolved to its reader paragraph and the
// char offset within that paragraph. Returns null when the point isn't over a
// [data-reader-seg] paragraph's text (e.g. the chapter heading or a gap). Used to
// read "which word sits at the top of the page" without depending on layout.
export function anchorAtPoint(x: number, y: number): ReaderAnchor | null {
  const caret = caretFromPoint(x, y);
  if (!caret) return null;
  const para = paragraphOf(caret.node);
  if (!para?.dataset.readerSeg) return null;
  return {
    segmentId: para.dataset.readerSeg,
    charOffset: offsetWithin(para, caret.node, caret.offset),
  };
}

// Word boundaries used for offsets: maximal runs of non-whitespace. Stable
// across screen sizes, so a word offset means the same word on any device.
const WORD_RE = /\S+/gu;

// Number of whitespace-delimited words in `text`.
export function countWords(text: string): number {
  const m = text.match(WORD_RE);
  return m ? m.length : 0;
}

// The word index that the char offset falls in (or just after): the count of
// words that begin at or before `charOffset`, minus one — i.e. the index of the
// word containing `charOffset`. Clamps to 0 before the first word. Inverse of
// wordOffsetToChar for word-start offsets.
export function charToWordOffset(text: string, charOffset: number): number {
  let n = 0;
  for (const m of text.matchAll(WORD_RE)) {
    if ((m.index ?? 0) > charOffset) break;
    n += 1;
  }
  return Math.max(0, n - 1);
}

// The char offset where the `wordOffset`-th word (0-based) starts. Clamps to the
// end of the text when the offset runs past the last word.
export function wordOffsetToChar(text: string, wordOffset: number): number {
  const target = Math.max(0, wordOffset);
  let i = 0;
  for (const m of text.matchAll(WORD_RE)) {
    if (i === target) return m.index ?? 0;
    i += 1;
  }
  return text.length;
}

// A DOM Range over [start, end) of a paragraph's text content.
export function rangeForOffsets(para: HTMLElement, start: number, end: number): Range {
  const a = locateOffset(para, start);
  const b = locateOffset(para, end);
  const range = document.createRange();
  range.setStart(a.node, a.offset);
  range.setEnd(b.node, b.offset);
  return range;
}

// Is point (pA, oA) strictly after (pB, oB) in document order? Works across
// different reader paragraphs (compares the elements when they differ).
export function pointAfter(pA: HTMLElement, oA: number, pB: HTMLElement, oB: number): boolean {
  if (pA === pB) return oA > oB;
  return !!(pA.compareDocumentPosition(pB) & Node.DOCUMENT_POSITION_PRECEDING);
}

// A DOM Range spanning two (paragraph, offset) points in document order — even
// when they sit in different paragraphs (a selection crossing a page boundary).
export function rangeBetween(pA: HTMLElement, oA: number, pB: HTMLElement, oB: number): Range {
  const flip = pointAfter(pA, oA, pB, oB);
  const start = flip ? locateOffset(pB, oB) : locateOffset(pA, oA);
  const end = flip ? locateOffset(pA, oA) : locateOffset(pB, oB);
  const range = document.createRange();
  range.setStart(start.node, start.offset);
  range.setEnd(end.node, end.offset);
  return range;
}

// Resolves a paragraph + char range into the ReaderSelection the WordingSheet
// consumes. Returns null for an empty or whitespace-only span.
export function readerSelectionFromOffsets(
  para: HTMLElement,
  start: number,
  end: number
): ReaderSelection | null {
  const segmentId = para.dataset.readerSeg;
  if (!segmentId) return null;
  const base = para.textContent ?? '';
  const s = Math.max(0, Math.min(start, end));
  const e = Math.min(base.length, Math.max(start, end));
  const text = base.slice(s, e);
  if (!text.trim()) return null;
  return {
    segmentId,
    text,
    base,
    start: s,
    end: e,
    rect: rangeForOffsets(para, s, e).getBoundingClientRect(),
  };
}

// Paints (range) or clears (null) our own selection tint via the CSS Custom
// Highlight API. The reader suppresses native selection (user-select: none) so
// mobile browsers don't raise their selection toolbar over the rewording sheet.
const HIGHLIGHT_NAME = 'bt-reader-selection';
export function paintSelection(range: Range | null): void {
  const w = window as unknown as {
    CSS?: { highlights?: { set(k: string, v: unknown): void; delete(k: string): void } };
    Highlight?: new (range: Range) => unknown;
  };
  const highlights = w.CSS?.highlights;
  if (!highlights || !w.Highlight) return;
  if (range) highlights.set(HIGHLIGHT_NAME, new w.Highlight(range));
  else highlights.delete(HIGHLIGHT_NAME);
}

// Splices `replacement` into `base` over the [start, end) range.
export function spliceText(base: string, start: number, end: number, replacement: string): string {
  return base.slice(0, start) + replacement + base.slice(end);
}

// The text immediately before/after a selection within its paragraph — handed
// to the suggestion service as fixed context so rewrites stay drop-in.
export function selectionContext(sel: ReaderSelection): { preceding: string; following: string } {
  return { preceding: sel.base.slice(0, sel.start), following: sel.base.slice(sel.end) };
}

export interface SourceSentence {
  start: number;
  end: number;
  text: string;
}

// Splits a passage into sentences, keeping trailing closers (quotes/brackets)
// and whitespace with the sentence they belong to. Used to let the reader step
// through the original sentence-by-sentence in the rewording sheet. Ported from
// TransliFlow.
export function splitSentences(source: string): SourceSentence[] {
  const res: SourceSentence[] = [];
  let start = 0;
  for (let i = 0; i < source.length; i += 1) {
    if (!'.!?…'.includes(source[i])) continue;
    let j = i + 1;
    while (j < source.length && /["'»”’)\]]/.test(source[j])) j += 1; // closers
    if (j < source.length && !/\s/.test(source[j])) continue; // mid-token dot
    while (j < source.length && /\s/.test(source[j])) j += 1; // trailing space
    res.push({ start, end: j, text: source.slice(start, j).trim() });
    start = j;
    i = j - 1;
  }
  if (start < source.length) {
    res.push({ start, end: source.length, text: source.slice(start).trim() });
  }
  return res.filter((s) => s.text.length > 0);
}

// Best-effort guess of which source sentence a target selection corresponds to,
// by proportional position. Approximate — the sheet lets the reader correct it
// with prev/next. Ported from TransliFlow.
export function likelySentenceIndex(
  base: string,
  start: number,
  end: number,
  sentences: SourceSentence[]
): number {
  const m = sentences.length;
  if (m === 0) return 0;
  const target = splitSentences(base);
  const n = target.length;
  if (n <= 1) return 0; // single (or no) translated sentence → first source one

  const mid = (start + end) / 2;
  let tIdx = target.findIndex((sn) => mid >= sn.start && mid < sn.end);
  if (tIdx < 0) tIdx = mid <= target[0].start ? 0 : n - 1; // gap → nearest end
  return Math.round((tIdx * (m - 1)) / (n - 1));
}
